// O Editor do Mentor — /circulo/editor (Círculo, parte 2, 15/10/2026).
// O desenho é docs/circulo-mentor/prototipo/editor-do-mentor.html.
//
// A casca (Visão geral + Perfil, Página, Série, Clube) e a aba Perfil: foto
// com recorte quadrado, nome, frase, bio e manifesto. A aba Série (parte 4)
// mora em editor-serie.tsx; a aba Clube (parte 5), em editor-clube.tsx.
//
// A aba Página (parte 3): modelo, paleta e seções (ligar, desligar e a ordem
// das abas; ao menos uma aba fica ligada), gravados por PATCH /pagina. A
// PRÉVIA é a página de verdade (/mentor/<slug>?previa=1) num iframe em escala,
// que recebe o rascunho por postMessage e se mostra como o assinante vê, antes
// de salvar. No computador largo ela fica ao lado; abaixo disso abre por um
// botão na barra de salvar e para logo acima dela (o Salvar segue à vista).
//
// O ASSISTENTE "Montar meu Círculo" (parte 6): introdução e 5 passos (Perfil →
// Modelo da página → 1ª série → Clube → Revisar), com os MESMOS formulários das
// abas — cada passo grava pela mesma API ao Avançar e, se a API recusar, fica no
// passo com o erro no campo. Abre sozinho para o Mentor ainda sem série; quem
// já tem série vai à Visão geral, que tem o link para reabri-lo. Quem sai no
// meio volta ao primeiro passo que falta, deduzido do que está salvo
// (passoQueFalta): nada guarda "em que passo ele estava".
//
// REGRA DO WESLEY: o Mentor monta tudo. Nenhum campo vem com texto pronto.
// Nada é cortado: passou do limite, o contador avisa e a API devolve o motivo
// (400 { error, campo }), que aparece junto do campo.
//
// TELA LIMPA (feedback do Wesley, 18/10): UMA descrição no fluxo — a bio, com
// o rótulo "Descrição" (a página mostra bio || manifesto). A frase curta e o
// manifesto saem do assistente e ficam na aba Perfil, em "Mais detalhes"
// recolhido; o que já está salvo continua salvo e na página. O rótulo basta:
// sem dicas nem legendas explicativas; no máximo uma linha curta por passo.
//
// ACESSO: só o próprio Mentor. A API responde 403 so_o_mentor para os outros,
// e quem chamou (App) leva para o início como um endereço que não existe.
//
// Visual: os tokens das paletas do app (index.css) — segue a paleta de quem
// está usando. Ocupa a janela inteira (não a coluna de 480 px do app): no
// celular a barra fica embaixo; no computador, um menu lateral.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { api } from '../../services/apiV2';
import { usoAcao } from '../uso';
import { Ic, Recorte, type RodapePasso } from './editor-comum';
import { AbaSerie } from './editor-serie';
import { AbaClube, resumoClube } from './editor-clube';
import './editor.css';

type Vista = 'vg' | 'perfil' | 'pagina' | 'serie' | 'clube';
type Campo = 'display_name' | 'tagline' | 'bio' | 'manifesto';
type Rascunho = Record<Campo, string> & { photo_url: string | null };
type Secao = { key: string; on: boolean };
type Pagina = { layout: string; paleta: string; secoes: Secao[] };
type Tela = 'celular' | 'computador';

const CAMPOS: Campo[] = ['display_name', 'tagline', 'bio', 'manifesto'];

// Os passos do assistente, na ordem (o passo N é PASSOS[N - 1]). No máximo
// uma linha curta por passo.
const PASSOS: { nome: string; lead?: string }[] = [
  { nome: 'Perfil' },
  { nome: 'Modelo da página', lead: 'Dá para trocar depois.' },
  { nome: '1ª série', lead: 'Os vídeos você sobe depois, no Estúdio.' },
  { nome: 'Clube' },
  { nome: 'Revisar', lead: 'Dá para mudar tudo depois.' },
];
const STATUS_SERIE: Record<string, string> = { rascunho: 'rascunho', publicando: 'publicando', concluida: 'concluída' };
const LIMITES_PADRAO: Record<Campo, number> = { display_name: 60, tagline: 80, bio: 240, manifesto: 800 };


const PARTES: { v: Vista; nome: string; ic: React.ReactNode; breve?: boolean }[] = [
  { v: 'vg', nome: 'Visão geral', ic: Ic.vg },
  { v: 'perfil', nome: 'Perfil', ic: Ic.perfil },
  { v: 'pagina', nome: 'Página', ic: Ic.pagina },
  { v: 'serie', nome: 'Série', ic: Ic.serie },
  { v: 'clube', nome: 'Clube', ic: Ic.clube },
];

// A página: os nomes são os do app; o desenho de cada modelo está em editor.css.
const MODELOS: [string, string][] = [['editorial', 'Editorial'], ['estante', 'Estante'], ['diario', 'Diário']];
const PALETAS: [string, string][] = [['midnight', 'Midnight'], ['ember', 'Âmbar'], ['vellum', 'Vellum']];
// Mapa e Sobre não são abas: o mapa abre a página (só para quem chega) e fica
// em cima; Sobre ainda não aparece na página. As quatro abas mudam de ordem.
const ABAS_PAGINA = ['conteudo', 'clube', 'estudo', 'cadernos'];
const SECAO_NOME: Record<string, string> = {
  mapa: 'Mapa de abertura', conteudo: 'Conteúdo', clube: 'Clube', estudo: 'Estudo', cadernos: 'Cadernos', sobre: 'Sobre',
};
const paginaDe = (m: any): Pagina => {
  const lista: Secao[] = Array.isArray(m?.page_sections) ? m.page_sections.map((x: any) => ({ key: x.key, on: !!x.on })) : [];
  const ord = (k: string) => (k === 'mapa' ? 0 : k === 'sobre' ? 2 : 1);
  return {
    layout: m?.page_layout || 'editorial',
    paleta: m?.page_palette || 'midnight',
    // O mapa em cima, as abas na ordem do Mentor, o Sobre no fim.
    secoes: lista.map((x, i) => [x, i] as const).sort((a, b) => ord(a[0].key) - ord(b[0].key) || a[1] - b[1]).map(([x]) => x),
  };
};
function diferencasPagina(base: Pagina, r: Pagina): Record<string, any> {
  const out: Record<string, any> = {};
  if (r.layout !== base.layout) out.page_layout = r.layout;
  if (r.paleta !== base.paleta) out.page_palette = r.paleta;
  if (JSON.stringify(r.secoes) !== JSON.stringify(base.secoes)) out.page_sections = r.secoes;
  return out;
}

// Conta caracteres como o servidor (um emoji é um só).
const contar = (s: string) => Array.from(s).length;
const rascunhoDe = (m: any): Rascunho => ({
  display_name: m?.display_name || '', tagline: m?.tagline || '', bio: m?.bio || '', manifesto: m?.manifesto || '',
  photo_url: m?.photo_url || null,
});
// O que mudou, já no formato da API (o servidor apara e grava vazio como nulo).
function diferencas(base: Rascunho, r: Rascunho): Record<string, string | null> {
  const out: Record<string, string | null> = {};
  for (const k of CAMPOS) if (r[k].trim() !== base[k].trim()) out[k] = r[k];
  if (r.photo_url !== base.photo_url) out.photo_url = r.photo_url;
  return out;
}

export const EditorDoMentor: React.FC<{
  session: any;
  onBack: () => void;
  onNaoEMentor: () => void;
  onVerPagina: (slug: string) => void;
  onAbrirClube: (clubId: string) => void;
  // Abrir já numa aba (o atalho "Publicar a série" da página, QA final D2).
  vistaInicial?: Vista;
}> = ({ session, onBack, onNaoEMentor, onVerPagina, onAbrirClube, vistaInicial }) => {
  const [dados, setDados] = useState<any>(null);
  const [falha, setFalha] = useState<string | null>(null);
  const [vista, setVista] = useState<Vista>(vistaInicial || 'vg');
  const [base, setBase] = useState<Rascunho | null>(null);
  const [rasc, setRasc] = useState<Rascunho | null>(null);
  const [erros, setErros] = useState<Record<string, string>>({});
  const [salvando, setSalvando] = useState(false);
  const [salvo, setSalvo] = useState(false);
  const [recorte, setRecorte] = useState<{ url: string; img: HTMLImageElement } | null>(null);
  const [basePag, setBasePag] = useState<Pagina | null>(null);
  const [rascPag, setRascPag] = useState<Pagina | null>(null);
  // A prévia: ao lado no computador largo; abaixo disso, aberta pelo botão.
  const [largo, setLargo] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.('(min-width: 1100px)').matches);
  const [previaAberta, setPreviaAberta] = useState(false);
  const [enviandoFoto, setEnviandoFoto] = useState(false);
  const [maisAberto, setMaisAberto] = useState(false);
  // O clube de cada série (o cartão "Clube" da Visão geral).
  const [clubes, setClubes] = useState<any>(null);
  // O assistente: null = fora dele; 0 = a introdução; 1 a 5 = os passos.
  const [passo, setPasso] = useState<number | null>(null);
  const [serieAssist, setSerieAssist] = useState<string | null>(null);
  const [seriesRev, setSeriesRev] = useState<any[] | null>(null);
  const decidiu = useRef(false);
  const arquivoRef = useRef<HTMLInputElement>(null);
  const rolagemRef = useRef<HTMLDivElement>(null);
  const salvarRef = useRef<HTMLDivElement>(null);
  // O App passa uma função nova a cada render: guardada aqui, não recarrega a tela.
  const naoEMentor = useRef(onNaoEMentor);
  // Rastro de uso: abrir o editor e cada troca de aba (v2/uso.ts).
  const vistaAnterior = useRef<string | null>(null);
  useEffect(() => {
    usoAcao(vistaAnterior.current === null ? 'editor_abrir' : 'editor_vista', { vista });
    vistaAnterior.current = vista;
  }, [vista]);
  naoEMentor.current = onNaoEMentor;

  const carregar = useCallback(async () => {
    setFalha(null);
    try {
      const d = await api.getEditor(session);
      setDados(d);
      // Na 1ª abertura: sem série ainda, o assistente vem primeiro.
      if (!decidiu.current) { decidiu.current = true; if (!d.series) setPasso(0); }
      setBase(rascunhoDe(d.mentor));
      setRasc(rascunhoDe(d.mentor));
      setBasePag(paginaDe(d.mentor));
      setRascPag(paginaDe(d.mentor));
    } catch (e: any) {
      if (e?.status === 403 || e?.status === 401) { naoEMentor.current(); return; }
      setFalha('Não deu para abrir o editor agora. Confira a conexão e tente de novo.');
    }
  }, [session]);
  useEffect(() => { carregar(); }, [carregar]);

  // Cada parte começa do topo, sem o aviso da outra.
  useEffect(() => { rolagemRef.current?.scrollTo?.({ top: 0 }); setSalvo(false); setErros({}); setPreviaAberta(false); }, [vista, passo]);
  useEffect(() => {
    const mq = window.matchMedia?.('(min-width: 1100px)');
    if (!mq) return;
    const mudou = () => setLargo(mq.matches);
    mq.addEventListener?.('change', mudou);
    return () => mq.removeEventListener?.('change', mudou);
  }, []);
  // O "Salvo" some sozinho e some na hora em que algo muda de novo.
  useEffect(() => { if (!salvo) return; const t = setTimeout(() => setSalvo(false), 4000); return () => clearTimeout(t); }, [salvo]);

  const limites: Record<Campo, number> = { ...LIMITES_PADRAO, ...(dados?.limites || {}) };
  const mudou = base && rasc ? diferencas(base, rasc) : {};
  const temMudanca = Object.keys(mudou).length > 0;

  const mudar = (k: Campo, v: string) => {
    setRasc((r) => (r ? { ...r, [k]: v } : r));
    setSalvo(false);
    if (erros[k]) setErros((e) => { const n = { ...e }; delete n[k]; return n; });
  };

  const mudouPag = basePag && rascPag ? diferencasPagina(basePag, rascPag) : {};
  const temMudancaPag = Object.keys(mudouPag).length > 0;
  const mudarPag = (f: (p: Pagina) => Pagina) => {
    setRascPag((p) => (p ? f(p) : p));
    setSalvo(false);
    setErros({});
  };
  // `tudo`: grava modelo, paleta e seções mesmo sem mudança (o passo do modelo
  // do assistente, para a página ficar gravada com o que ele viu).
  const salvarPagina = async (tudo = false): Promise<boolean> => {
    if (!rascPag || (!tudo && !temMudancaPag) || salvando) return false;
    setSalvando(true); setSalvo(false); setErros({});
    try {
      const d = await api.salvarPaginaEditor(session, tudo
        ? { page_layout: rascPag.layout, page_palette: rascPag.paleta, page_sections: rascPag.secoes } : mudouPag);
      setDados(d);
      setBasePag(paginaDe(d.mentor));
      setRascPag(paginaDe(d.mentor));
      setSalvo(true);
      return true;
    } catch (e: any) {
      if (e?.status === 403) { naoEMentor.current(); return false; }
      const texto = e?.status === 400 ? (e?.dados?.error || e.message) : 'Não deu para salvar agora. Confira a conexão e tente de novo.';
      setErros({ [e?.status === 400 && e?.dados?.campo === 'page_sections' ? 'secoes' : '_geral']: texto });
      return false;
    } finally {
      setSalvando(false);
    }
  };

  const salvar = async (): Promise<boolean> => {
    if (!rasc || !temMudanca || salvando) return false;
    setSalvando(true); setSalvo(false); setErros({});
    try {
      const d = await api.salvarPerfilEditor(session, mudou);
      setDados(d);
      setBase(rascunhoDe(d.mentor));
      setRasc(rascunhoDe(d.mentor));
      setSalvo(true);
      return true;
    } catch (e: any) {
      if (e?.status === 403) { naoEMentor.current(); return false; }
      const campo = e?.status === 400 && e?.dados?.campo;
      const texto = e?.status === 400 ? (e?.dados?.error || e.message) : 'Não deu para salvar agora. Confira a conexão e tente de novo.';
      setErros({ [campo || '_geral']: texto });
      if (campo) {
        const el = document.getElementById(`ced-${campo}`);
        el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
        (el as HTMLElement | null)?.focus?.({ preventScroll: true });
      }
      return false;
    } finally {
      setSalvando(false);
    }
  };

  // A Visão geral conta as séries e resume o clube de cada uma; as abas
  // Série e Clube avisam quando algo muda.
  const lerClubes = useCallback(async () => {
    try { setClubes(await api.getClubesEditor(session)); } catch (_) { /* fica o resumo de antes */ }
  }, [session]);
  useEffect(() => { lerClubes(); }, [lerClubes]);
  const contarSeries = async () => {
    lerClubes();
    try { const d = await api.getEditor(session); setDados((x: any) => (x ? { ...x, series: d.series } : d)); } catch (_) { /* fica a contagem de antes */ }
  };

  // ---- foto ----
  const escolherFoto = (f: File | undefined) => {
    if (!f) return;
    setErros((e) => { const n = { ...e }; delete n.photo_url; return n; });
    if (!/^image\//.test(f.type)) { setErros((e) => ({ ...e, photo_url: 'Esse arquivo não é uma imagem. Use JPEG, PNG ou WebP.' })); return; }
    const url = URL.createObjectURL(f);
    const img = new Image();
    img.onload = () => setRecorte({ url, img });
    img.onerror = () => { URL.revokeObjectURL(url); setErros((e) => ({ ...e, photo_url: 'Não deu para abrir essa imagem. Use JPEG, PNG ou WebP.' })); };
    img.src = url;
  };
  const fecharRecorte = () => { if (recorte) URL.revokeObjectURL(recorte.url); setRecorte(null); };
  const usarRecorte = async (blob: Blob) => {
    fecharRecorte();
    setEnviandoFoto(true);
    try {
      const r = await api.enviarFotoEditor(session, blob);
      setRasc((x) => (x ? { ...x, photo_url: r.photo_url } : x));
      setSalvo(false);
    } catch (e: any) {
      setErros((x) => ({ ...x, photo_url: e?.dados?.error || 'Não deu para enviar a foto. Tente de novo.' }));
    } finally {
      setEnviandoFoto(false);
    }
  };

  if (falha) {
    return (
      <div className="ced" data-editor="">
        <Topo onBack={onBack} />
        <div className="ced-aviso"><p>{falha}</p><button className="ced-btn ced-btn-1" onClick={carregar}>Tentar de novo</button></div>
      </div>
    );
  }
  if (!dados || !rasc) {
    return <div className="ced" data-editor=""><Topo onBack={onBack} /><div className="ced-aviso" aria-busy="true"><p>Abrindo o editor…</p></div></div>;
  }

  const m = dados.mentor;
  const nomeSalvo = (base?.display_name || '').trim();
  const primeiroNome = nomeSalvo.split(/\s+/)[0];
  const inicial = (Array.from(rasc.display_name.trim() || nomeSalvo || '?')[0] || '?').toUpperCase();
  const foto = rasc.photo_url;

  // ---- o assistente: em que passo está e para onde vai ----
  // A 1ª série é a mais antiga (a lista vem da mais nova para a mais antiga).
  const primeiraSerie = clubes?.series?.length ? clubes.series[clubes.series.length - 1] : null;
  const idSerie: string | null = serieAssist || primeiraSerie?.id || null;
  const clubeDaSerie = clubes?.series?.find((s: any) => s.id === idSerie) || null;
  // O que já está feito, pelo que está salvo: nome e bio; a página gravada ao
  // menos uma vez; uma série; um clube que a página mostre. O primeiro passo
  // que falta é onde o assistente retoma (sem nada a fazer, no Revisar).
  const feitos = [
    !!base?.display_name.trim() && !!base?.bio.trim(),
    !!m.pagina_gravada,
    !!dados.series,
    !!clubeDaSerie?.vinculo,
  ];
  const falta = feitos.indexOf(false) + 1 || 5;
  const irPara = (n: number) => {
    setPasso(n);
    if (n === 5) {
      lerClubes();
      // Mantém o que já leu enquanto relê: o Revisar não pisca nem pula.
      api.getSeriesEditor(session).then((d) => setSeriesRev(d.series || [])).catch(() => setSeriesRev([]));
    }
  };
  const sairDoAssistente = () => { setPasso(null); setVista('vg'); contarSeries(); };
  const avancarPerfil = async () => { if (temMudanca && !(await salvar())) return; irPara(2); };
  const avancarModelo = async () => { if (await salvarPagina(true)) irPara(3); };

  const vg = (
    <section className="ced-vg" data-vista-vg="">
      <span className="ced-eyebrow">Círculo · editor</span>
      <h1 className="ced-titulo">{primeiroNome ? `Olá, ${primeiroNome}` : 'Seu Círculo'}</h1>
      <button className="ced-cartao cas-montar" data-acao="montar-circulo" data-falta={falta} onClick={() => setPasso(0)}>
        <span className="ced-cartao-ic">{Ic.varinha}</span>
        <span className="ced-cartao-tx"><b>Montar meu Círculo</b>
          {falta < 5 && <span>Falta: {PASSOS[falta - 1].nome}</span>}</span>
        <span className="ced-ir">{Ic.ir}</span>
      </button>
      <div className="ced-cartoes">
        <button className="ced-cartao" data-cartao="perfil" onClick={() => setVista('perfil')}>
          <span className="ced-cartao-ic">{Ic.perfil}</span>
          <span className="ced-cartao-tx"><b>Perfil</b>
            <span>{nomeSalvo || 'sem nome'}{base?.bio || base?.manifesto ? '' : ' · sem descrição'}</span></span>
          <span className="ced-ir">{Ic.ir}</span>
        </button>
        {basePag && (
          <button className="ced-cartao" data-cartao="pagina" onClick={() => setVista('pagina')}>
            <span className="ced-cartao-ic">{Ic.pagina}</span>
            <span className="ced-cartao-tx"><b>Página</b>
              <span>Modelo {nomeDe(MODELOS, basePag.layout)} · {nomeDe(PALETAS, basePag.paleta)} · {abasLigadas(basePag)}</span></span>
            <span className="ced-ir">{Ic.ir}</span>
          </button>
        )}
        <button className="ced-cartao" data-cartao="serie" onClick={() => setVista('serie')}>
          <span className="ced-cartao-ic">{Ic.serie}</span>
          <span className="ced-cartao-tx"><b>Série</b>
            <span>{dados.series ? `${dados.series} ${dados.series === 1 ? 'série' : 'séries'}` : 'nenhuma série ainda'}</span></span>
          <span className="ced-ir">{Ic.ir}</span>
        </button>
        <button className="ced-cartao" data-cartao="clube" onClick={() => setVista('clube')}>
          <span className="ced-cartao-ic">{Ic.clube}</span>
          <span className="ced-cartao-tx"><b>Clube</b>
            {!clubes ? <span>…</span>
              : !clubes.series?.length ? <span>crie uma série primeiro</span>
                : clubes.series.map((s: any) => (
                  <span key={s.id} data-resumo-clube={s.id} data-vinculo={s.vinculo || 'nenhum'}>
                    {clubes.series.length > 1 ? `${s.titulo || s.livro?.titulo}: ` : ''}{resumoClube(s)}
                  </span>
                ))}</span>
          <span className="ced-ir">{Ic.ir}</span>
        </button>
        {PARTES.filter((p) => p.breve).map((p) => (
          <div key={p.v} className="ced-cartao" data-cartao={p.v} aria-disabled="true">
            <span className="ced-cartao-ic">{p.ic}</span>
            <span className="ced-cartao-tx"><b>{p.nome}</b><span>Chega nas próximas versões do editor.</span></span>
            <span className="ced-breve">em breve</span>
          </div>
        ))}
      </div>
    </section>
  );

  const contador = (k: Campo) => {
    const n = contar(rasc[k]);
    const lim = limites[k];
    return <span className="ced-cnt" data-cnt={k} data-estado={n > lim ? 'passou' : n >= lim * 0.92 ? 'perto' : ''}>{n}/{lim}</span>;
  };
  const erroDe = (k: string) => erros[k] ? <p className="ced-erro" role="alert" data-erro={k}>{erros[k]}</p> : null;
  const barraSalvar = (tem: boolean, onSalvar: () => void, ocupado: boolean, extra?: React.ReactNode) => (
    <div className="ced-salvar" data-salvar="" ref={salvarRef}>
      <div className="ced-salvar-estado" aria-live="polite">
        {salvo
          ? <span className="ced-salvo" role="status" data-salvo="">{Ic.ok}Salvo</span>
          : erros._geral ? <span className="ced-erro" role="alert" data-erro="_geral">{erros._geral}</span>
            : tem ? <span className="ced-nota">Há mudanças sem salvar.</span>
              : <span className="ced-nota">Tudo salvo.</span>}
      </div>
      {extra}
      <button className="ced-btn ced-btn-1" data-acao="salvar" onClick={onSalvar} disabled={!tem || salvando || ocupado}>
        {salvando ? 'Salvando…' : 'Salvar'}
      </button>
    </div>
  );

  // Os campos do perfil: a aba Perfil e o 1º passo do assistente. O assistente
  // pede só foto, nome e a descrição; a frase curta e o manifesto ficam na aba,
  // em "Mais detalhes" (aberto sozinho se a API recusar um deles).
  const preenchidos = [base?.tagline && 'frase', base?.manifesto && 'manifesto'].filter(Boolean).join(' · ');
  const campoTexto = (k: Campo, rotulo: string, linhas?: number) => (
    <div className="ced-campo" data-campo={k} data-invalido={erros[k] ? '1' : undefined}>
      <label htmlFor={`ced-${k}`}><span>{rotulo}</span>{contador(k)}</label>
      {linhas
        ? <textarea id={`ced-${k}`} rows={linhas} value={rasc[k]} onChange={(e) => mudar(k, e.target.value)} />
        : <input id={`ced-${k}`} type="text" value={rasc[k]} autoComplete={k === 'display_name' ? 'name' : 'off'} onChange={(e) => mudar(k, e.target.value)} />}
      {erroDe(k)}
    </div>
  );
  const camposPerfil = (comMais: boolean) => (
    <>
      <div className="ced-foto" data-campo="photo_url">
        <div className="ced-avatar" data-avatar="" data-tem-foto={foto ? '1' : '0'}>
          {foto ? <img src={foto} alt="Sua foto" /> : <span aria-hidden="true">{inicial}</span>}
        </div>
        <div className="ced-foto-acoes">
          <div className="ced-linha">
            <button className="ced-btn ced-btn-2 ced-btn-sm" data-acao="enviar-foto" disabled={enviandoFoto}
              onClick={() => arquivoRef.current?.click()}>{Ic.camera}{enviandoFoto ? 'Enviando…' : foto ? 'Trocar foto' : 'Enviar foto'}</button>
            {foto && !enviandoFoto && <button className="ced-btn ced-btn-q ced-btn-sm" data-acao="tirar-foto"
              onClick={() => { setRasc((x) => (x ? { ...x, photo_url: null } : x)); setSalvo(false); }}>Remover foto</button>}
          </div>
          {erroDe('photo_url')}
        </div>
        <input ref={arquivoRef} type="file" accept="image/*" hidden data-input-foto=""
          onChange={(e) => { escolherFoto(e.target.files?.[0]); e.target.value = ''; }} />
      </div>

      {campoTexto('display_name', 'Nome')}
      {campoTexto('bio', 'Descrição', 4)}
      {comMais && (
        <details className="ced-mais" data-mais-detalhes="" open={maisAberto || !!erros.tagline || !!erros.manifesto}
          onToggle={(e) => setMaisAberto((e.currentTarget as HTMLDetailsElement).open)}>
          <summary>{Ic.descer}<span>Mais detalhes</span>{preenchidos && <small>{preenchidos}</small>}</summary>
          {campoTexto('tagline', 'Frase curta')}
          {campoTexto('manifesto', 'Manifesto', 6)}
        </details>
      )}
    </>
  );
  const perfil = (
    <section className="ced-perfil" data-vista-perfil="">
      <span className="ced-eyebrow">Círculo · editor</span>
      <h1 className="ced-titulo">Perfil</h1>
      {camposPerfil(true)}
      {barraSalvar(temMudanca, salvar, enviandoFoto)}
    </section>
  );

  // ---- página: modelo, paleta, seções ----
  const pag = rascPag || paginaDe(m);
  const mapaSec = pag.secoes.find((x) => x.key === 'mapa');
  const abasSec = pag.secoes.filter((x) => ABAS_PAGINA.includes(x.key));
  const sobreSec = pag.secoes.find((x) => x.key === 'sobre');
  const montar = (abas: Secao[], mapa = mapaSec, sobre = sobreSec): Secao[] =>
    [mapa, ...abas, sobre].filter(Boolean) as Secao[];
  const alternar = (k: string) => {
    if (k === 'mapa') { mudarPag((p) => ({ ...p, secoes: montar(abasSec, mapaSec && { ...mapaSec, on: !mapaSec.on }) })); return; }
    const alvo = abasSec.find((x) => x.key === k);
    if (!alvo) return;
    // A última aba ligada não desliga: a página ficaria sem nada para abrir.
    if (alvo.on && abasSec.filter((x) => x.on).length === 1) {
      setErros({ secoes: 'Deixe ligada ao menos uma destas seções: Conteúdo, Clube, Estudo ou Cadernos.' });
      return;
    }
    mudarPag((p) => ({ ...p, secoes: montar(abasSec.map((x) => (x.key === k ? { ...x, on: !x.on } : x))) }));
  };
  const mover = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= abasSec.length) return;
    const n = abasSec.slice();
    [n[i], n[j]] = [n[j], n[i]];
    mudarPag((p) => ({ ...p, secoes: montar(n) }));
  };
  const itemSecao = (x: Secao, i: number | null, nAbas = 0) => {
    const nome = SECAO_NOME[x.key] || x.key;
    const breve = x.key === 'sobre';
    return (
      <li key={x.key} className="ced-secao" data-secao={x.key} data-on={x.on && !breve ? '1' : '0'}>
        <span className="ced-secao-nome"><b>{nome}</b></span>
        {i !== null && (
          <span className="ced-mover">
            <button aria-label={`Subir ${nome}`} data-acao="subir" disabled={i === 0} onClick={() => mover(i, -1)}>{Ic.subir}</button>
            <button aria-label={`Descer ${nome}`} data-acao="descer" disabled={i === nAbas - 1} onClick={() => mover(i, 1)}>{Ic.descer}</button>
          </span>
        )}
        {breve
          ? <span className="ced-breve">em breve</span>
          : <button className="ced-sw" role="switch" aria-checked={x.on} aria-label={`${nome}: ${x.on ? 'ligada' : 'desligada'}`}
              data-acao="alternar" onClick={() => alternar(x.key)}><i /></button>}
      </li>
    );
  };

  // O modelo e a paleta: a aba Página e o 2º passo do assistente.
  const escolhaModelo = (
    <>
      <div className="ced-secao-titulo"><h3>Modelo</h3></div>
      <div className="ced-modelos" role="group" aria-label="Modelo da página">
        {MODELOS.map(([k, nome]) => (
          <button key={k} className="ced-modelo" aria-pressed={pag.layout === k} data-modelo={k}
            onClick={() => mudarPag((p) => ({ ...p, layout: k }))}>
            <span className={`ced-modelo-mini m-${k}`} aria-hidden="true"><i /><i /><i /><i /></span>
            <b>{nome}</b>
          </button>
        ))}
      </div>

      <div className="ced-secao-titulo"><h3>Paleta</h3></div>
      <div className="ced-paletas" role="group" aria-label="Paleta da página">
        {PALETAS.map(([k, nome]) => (
          <button key={k} className="ced-paleta" aria-pressed={pag.paleta === k} data-paleta-opcao={k}
            onClick={() => mudarPag((p) => ({ ...p, paleta: k }))}>
            {/* A amostra é um pedacinho da própria página, com os tokens da paleta. */}
            <span className="cm ced-amostra" data-paleta={k} aria-hidden="true"><i /><i /><i /></span>
            <span>{nome}</span>
          </button>
        ))}
      </div>
    </>
  );
  const pagina = (
    <section className="ced-pagina" data-vista-pagina="">
      <span className="ced-eyebrow">Círculo · editor</span>
      <h1 className="ced-titulo">Página</h1>
      {escolhaModelo}

      <div className="ced-secao-titulo"><h3>Seções</h3></div>
      <ul className="ced-secoes" data-secoes="">
        {mapaSec && itemSecao(mapaSec, null)}
        {abasSec.map((x, i) => itemSecao(x, i, abasSec.length))}
        {sobreSec && itemSecao(sobreSec, null)}
      </ul>
      {erroDe('secoes')}

      {barraSalvar(temMudancaPag, () => salvarPagina(), false,
        !largo && <button className="ced-btn ced-btn-2" data-acao="ver-previa" onClick={() => setPreviaAberta(true)}>{Ic.olho}<span>Prévia</span></button>)}
    </section>
  );

  // ---- o assistente: a tela ----
  const rodape = (o: { pronto: boolean; ocupado: boolean; avancar: () => void; extra?: React.ReactNode }) => (
    <div className="ced-salvar cas-rodape" data-rodape-assistente="" ref={salvarRef}>
      <button className="ced-btn ced-btn-q" data-acao="voltar-passo" onClick={() => setPasso((p) => Math.max(0, (p || 1) - 1))}>
        {Ic.left}<span>Voltar</span></button>
      <div className="ced-salvar-estado" aria-live="polite">
        {erros._geral && <span className="ced-erro" role="alert" data-erro="_geral">{erros._geral}</span>}
      </div>
      {o.extra}
      <button className="ced-btn ced-btn-1" data-acao="avancar" onClick={o.avancar} disabled={!o.pronto || o.ocupado || salvando}>
        {o.ocupado || salvando ? 'Salvando…' : 'Avançar'}
      </button>
    </div>
  );
  const rodapePasso: RodapePasso = (o) => rodape(o);
  const cabecaPasso = (n: number) => (
    <>
      <h1 className="ced-titulo">{PASSOS[n - 1].nome}</h1>
      {PASSOS[n - 1].lead && <p className="ced-lead">{PASSOS[n - 1].lead}</p>}
    </>
  );
  const editar = (n: number) => (
    <button className="ced-btn ced-btn-q ced-btn-sm" data-editar={n} onClick={() => setPasso(n)}>{Ic.lapis}<span>editar</span></button>
  );
  const serieRev = seriesRev?.find((s) => s.id === idSerie) || null;

  const intro = (
    <section className="cas-intro" data-passo="0">
      <span className="cas-glifo" aria-hidden="true">{Ic.varinha}</span>
      <h1 className="ced-titulo">Montar meu Círculo</h1>
      <p className="ced-lead">Dá para parar e continuar depois.</p>
      <ol className="cas-lista">
        {PASSOS.map((p, i) => (
          <li key={p.nome} data-item-passo={i + 1} data-feito={feitos[i] ? '1' : '0'}>
            <span className="cas-num">{feitos[i] ? Ic.ok : i + 1}</span>
            <span><b>{p.nome}</b></span>
          </li>
        ))}
      </ol>
      <div className="cas-intro-acoes">
        <button className="ced-btn ced-btn-1" data-acao="comecar" onClick={() => irPara(falta)}>
          {falta > 1 ? 'Continuar de onde parei' : 'Começar agora'}</button>
        {falta > 1 && <button className="ced-btn ced-btn-q" data-acao="rever-desde-perfil" onClick={() => irPara(1)}>Rever desde o perfil</button>}
      </div>
    </section>
  );

  const revisar = (
    <section className="cas-revisar" data-passo="5">
      {cabecaPasso(5)}
      <div className="cas-bloco" data-bloco="perfil">
        <div className="cas-bloco-cab"><b>Perfil</b>{editar(1)}</div>
        <div className="cas-perfil">
          <span className="ced-avatar cas-avatar" aria-hidden="true">{base?.photo_url ? <img src={base.photo_url} alt="" /> : <span>{inicial}</span>}</span>
          <span><b>{nomeSalvo || 'sem nome'}</b>{base?.tagline && <small>{base.tagline}</small>}</span>
        </div>
        {base?.bio || base?.manifesto ? <p className="cas-texto">{base?.bio || base?.manifesto}</p> : <p className="ced-nota">sem descrição</p>}
      </div>
      <div className="cas-bloco" data-bloco="pagina">
        <div className="cas-bloco-cab"><b>Página</b>{editar(2)}</div>
        {basePag && <p className="cas-texto">Modelo {nomeDe(MODELOS, basePag.layout)} · {nomeDe(PALETAS, basePag.paleta)} · {abasLigadas(basePag)}</p>}
      </div>
      <div className="cas-bloco" data-bloco="serie">
        <div className="cas-bloco-cab"><b>Série</b>{editar(3)}</div>
        {!seriesRev ? <p className="ced-nota">…</p>
          : !serieRev ? <p className="ced-nota">nenhuma série ainda</p>
            : <>
              <p className="cas-texto"><b>{serieRev.titulo}</b>
                <small>{[serieRev.livro?.titulo, serieRev.livro?.autor].filter(Boolean).join(', ')}</small></p>
              <p className="ced-nota">
                {serieRev.episodios === 1 ? '1 episódio' : `${serieRev.episodios} episódios`} · {STATUS_SERIE[serieRev.status] || serieRev.status}
              </p>
              {serieRev.status === 'rascunho' && <p className="ced-nota" data-nota-rascunho="">Rascunho: ainda não aparece na página.</p>}
            </>}
      </div>
      <div className="cas-bloco" data-bloco="clube">
        <div className="cas-bloco-cab"><b>Clube</b>{editar(4)}</div>
        <p className="cas-texto">{clubeDaSerie ? resumoClube(clubeDaSerie) : '…'}</p>
      </div>
      <div className="cas-pronto">
        <span className="cas-endereco" data-endereco="">forjamente.com.br/mentor/{m.slug}</span>
        <button className="ced-btn ced-btn-1" data-acao="ver-minha-pagina" onClick={() => onVerPagina(m.slug)}>{Ic.olho}<span>Ver minha página</span></button>
      </div>
      {/* Aqui nada fica por gravar: o rodapé fica no fim, sem cobrir "Ver minha página". */}
      <div className="ced-salvar cas-rodape cas-rodape-solto" data-rodape-assistente="">
        <button className="ced-btn ced-btn-q" data-acao="voltar-passo" onClick={() => setPasso(4)}>{Ic.left}<span>Voltar</span></button>
        <div className="ced-salvar-estado" />
        <button className="ced-btn ced-btn-2" data-acao="ir-editor" onClick={sairDoAssistente}>Ir para o editor</button>
      </div>
    </section>
  );

  const passoAtual = passo === 0 ? intro
    : passo === 1 ? (
      <section className="ced-perfil" data-passo="1">
        {cabecaPasso(1)}
        {camposPerfil(false)}
        {rodape({ pronto: !!rasc.display_name.trim() && !!rasc.bio.trim(), ocupado: enviandoFoto, avancar: avancarPerfil })}
      </section>
    ) : passo === 2 ? (
      <section className="ced-pagina" data-passo="2">
        {cabecaPasso(2)}
        {escolhaModelo}
        {erroDe('secoes')}
        {rodape({ pronto: true, ocupado: false, avancar: avancarModelo,
          extra: !largo && <button className="ced-btn ced-btn-2" data-acao="ver-previa" onClick={() => setPreviaAberta(true)}>{Ic.olho}<span>Prévia</span></button> })}
      </section>
    ) : passo === 3 ? (
      <section data-passo="3">
        {cabecaPasso(3)}
        {/* Já há série, mas a lista ainda não chegou: espera, para não criar outra. */}
        {dados.series && !idSerie ? <div className="ced-aviso" aria-busy="true"><p>Abrindo a série…</p></div>
          : <AbaSerie key={idSerie || 'nova'} session={session} onNaoEMentor={onNaoEMentor} onMudou={contarSeries}
              rolarTopo={() => rolagemRef.current?.scrollTo?.({ top: 0 })}
              assistente={{ serieId: idSerie, rodape: rodapePasso, onAvancou: (id) => { setSerieAssist(id); irPara(4); } }} />}
      </section>
    ) : passo === 4 ? (
      <section data-passo="4">
        {cabecaPasso(4)}
        <AbaClube session={session} onNaoEMentor={onNaoEMentor} onAbrirClube={onAbrirClube} onIrSerie={() => setPasso(3)}
          onMudou={setClubes} assistente={{ serieId: idSerie, rodape: rodapePasso, onAvancou: () => irPara(5) }} />
      </section>
    ) : revisar;

  if (passo !== null) {
    const n = passo;
    return (
      <div className="ced cas" data-editor="" data-assistente="" data-passo-atual={n}>
        <header className="ced-topo ced-topo-assist">
          <div className="ced-marca"><b>Montar meu Círculo</b><span data-progresso-texto="">{n ? `passo ${n} de 5` : 'o passo a passo'}</span></div>
          <div className="ced-espaco" />
          <button className="ced-btn ced-btn-q ced-btn-sm" data-acao="pular-assistente" onClick={sairDoAssistente}>Pular e ir para o editor</button>
        </header>
        <div className="cas-progresso" role="progressbar" aria-label="Progresso" aria-valuemin={0} aria-valuemax={5} aria-valuenow={n}>
          <i style={{ width: `${(n / 5) * 100}%` }} />
        </div>
        <div className="ced-corpo">
          <div className="ced-rolagem" ref={rolagemRef}>
            <main className="ced-main">{passoAtual}</main>
          </div>
          {n === 2 && largo && (
            <aside className="ced-previa-lado" data-previa-lado=""><Previa slug={m.slug} pagina={pag} /></aside>
          )}
        </div>
        {n === 2 && !largo && previaAberta && (
          <PreviaFolha salvarRef={salvarRef} onFechar={() => setPreviaAberta(false)}><Previa slug={m.slug} pagina={pag} onFechar={() => setPreviaAberta(false)} /></PreviaFolha>
        )}
        {recorte && <Recorte img={recorte.img} onCancelar={fecharRecorte} onUsar={usarRecorte} />}
      </div>
    );
  }

  return (
    <div className="ced" data-editor="" data-vista={vista}>
      <Topo onBack={onBack} onVerPagina={() => onVerPagina(m.slug)} />
      <div className="ced-corpo">
        <nav className="ced-nav" aria-label="Partes do editor">
          <span className="ced-nav-grupo ced-so-largo">Seu Círculo</span>
          {PARTES.map((p) => (
            <button key={p.v} data-parte={p.v} aria-current={vista === p.v ? 'page' : undefined}
              disabled={p.breve} title={p.breve ? `${p.nome}: em breve` : undefined} onClick={() => setVista(p.v)}>
              {p.ic}<span>{p.v === 'vg' ? <><span className="ced-so-largo">Visão geral</span><span className="ced-so-estreito">Geral</span></> : p.nome}</span>
              {p.breve && <small className="ced-so-largo">em breve</small>}
            </button>
          ))}
        </nav>
        <div className="ced-rolagem" ref={rolagemRef}>
          <main className="ced-main">{vista === 'perfil' ? perfil : vista === 'pagina' ? pagina
            : vista === 'serie' ? <AbaSerie session={session} onNaoEMentor={onNaoEMentor} onMudou={contarSeries}
              rolarTopo={() => rolagemRef.current?.scrollTo?.({ top: 0 })} />
            : vista === 'clube' ? <AbaClube session={session} onNaoEMentor={onNaoEMentor} onAbrirClube={onAbrirClube}
              onIrSerie={() => setVista('serie')} onMudou={setClubes} /> : vg}</main>
        </div>
        {vista === 'pagina' && largo && (
          <aside className="ced-previa-lado" data-previa-lado=""><Previa slug={m.slug} pagina={pag} /></aside>
        )}
      </div>
      {vista === 'pagina' && !largo && previaAberta && (
        <PreviaFolha salvarRef={salvarRef} onFechar={() => setPreviaAberta(false)}><Previa slug={m.slug} pagina={pag} onFechar={() => setPreviaAberta(false)} /></PreviaFolha>
      )}
      {recorte && <Recorte img={recorte.img} onCancelar={fecharRecorte} onUsar={usarRecorte} />}
    </div>
  );
};

const nomeDe = (lista: [string, string, ...string[]][], k: string) => (lista.find((x) => x[0] === k) || [k, k])[1];
const abasLigadas = (p: Pagina) => {
  const n = p.secoes.filter((x) => ABAS_PAGINA.includes(x.key) && x.on).length;
  return n === 1 ? '1 aba ligada' : `${n} abas ligadas`;
};

// ---- prévia: a página real num iframe em escala, com o rascunho ----
const TELAS: Record<Tela, { w: number; h: number }> = { celular: { w: 390, h: 844 }, computador: { w: 1280, h: 800 } };

const Previa: React.FC<{ slug: string; pagina: Pagina; onFechar?: () => void }> = ({ slug, pagina, onFechar }) => {
  const [tela, setTela] = useState<Tela>('celular');
  // Cada "pronta" do iframe (a página montou) pede o rascunho de novo.
  const [pronta, setPronta] = useState(0);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const caixaRef = useRef<HTMLDivElement>(null);
  const [caixa, setCaixa] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = caixaRef.current;
    if (!el) return;
    const medir = () => setCaixa({ w: el.clientWidth, h: el.clientHeight });
    medir();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(medir) : null;
    ro?.observe(el);
    window.addEventListener('resize', medir);
    return () => { ro?.disconnect(); window.removeEventListener('resize', medir); };
  }, []);
  useEffect(() => {
    const ouvir = (e: MessageEvent) => {
      if (e.origin !== window.location.origin || e.source !== frameRef.current?.contentWindow) return;
      if (e.data?.tipo === 'circulo-previa-pronta') setPronta((n) => n + 1);
    };
    window.addEventListener('message', ouvir);
    return () => window.removeEventListener('message', ouvir);
  }, []);
  useEffect(() => {
    if (pronta) frameRef.current?.contentWindow?.postMessage({ tipo: 'circulo-previa', pagina }, window.location.origin);
  }, [pronta, pagina]);

  const { w, h } = TELAS[tela];
  const escala = caixa.w && caixa.h ? Math.min(caixa.w / w, caixa.h / h) : 0;
  return (
    <div className="ced-previa" data-previa="" data-tela={tela}>
      <div className="ced-previa-cab">
        <b>Prévia</b>
        <span className="ced-telas" role="group" aria-label="Tamanho da tela">
          <button aria-pressed={tela === 'celular'} aria-label="Celular" data-tela-opcao="celular" onClick={() => setTela('celular')}>{Ic.celular}</button>
          <button aria-pressed={tela === 'computador'} aria-label="Computador" data-tela-opcao="computador" onClick={() => setTela('computador')}>{Ic.computador}</button>
        </span>
        {onFechar && <button className="ced-icone" aria-label="Fechar a prévia" data-acao="fechar-previa" onClick={onFechar}>{Ic.fechar}</button>}
      </div>
      <div className="ced-previa-caixa" ref={caixaRef}>
        <div className="ced-previa-moldura" style={{ width: w * escala, height: h * escala }}>
          <iframe ref={frameRef} title="Prévia da sua página" src={`/mentor/${encodeURIComponent(slug)}?previa=1`} data-previa-frame=""
            style={{ width: w, height: h, transform: `scale(${escala})` }} />
        </div>
      </div>
    </div>
  );
};

// No celular a prévia cobre a tela de cima até logo acima da barra de salvar.
const PreviaFolha: React.FC<{ salvarRef: React.RefObject<HTMLDivElement | null>; onFechar: () => void; children: React.ReactNode }> = ({ salvarRef, onFechar, children }) => {
  const [base, setBase] = useState(0);
  useLayoutEffect(() => {
    const medir = () => {
      const r = salvarRef.current?.getBoundingClientRect();
      setBase(r ? Math.max(0, window.innerHeight - r.top) : 0);
    };
    medir();
    window.addEventListener('resize', medir);
    return () => window.removeEventListener('resize', medir);
  }, [salvarRef]);
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onFechar(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onFechar]);
  return <div className="ced-previa-folha" role="dialog" aria-label="Prévia da página" data-previa-folha="" style={{ bottom: base }}>{children}</div>;
};

const Topo: React.FC<{ onBack: () => void; onVerPagina?: () => void }> = ({ onBack, onVerPagina }) => (
  <header className="ced-topo">
    <button className="ced-icone" onClick={onBack} aria-label="Voltar">{Ic.left}</button>
    <div className="ced-marca"><b>Forjamente</b><span>Círculo · Editor</span></div>
    <div className="ced-espaco" />
    {onVerPagina && <button className="ced-btn ced-btn-2 ced-btn-sm" data-acao="ver-pagina" onClick={onVerPagina}>{Ic.olho}<span>Ver minha página</span></button>}
  </header>
);

